import React, { useState, useEffect, useMemo } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import "./InboundDashboard.css";

// ── Inline area chart — no external deps ─────────────────────────────────────
const AreaChart = ({ data = [], color = "#459E74" }) => {
    const W = 600, H = 148;
    const PAD = { top: 18, right: 12, bottom: 30, left: 36 };
    const iW = W - PAD.left - PAD.right;
    const iH = H - PAD.top  - PAD.bottom;

    const pts = useMemo(() => {
        if (!data.length) return null;
        const maxVal = Math.max(...data.map(d => d.count), 1);
        const n = data.length;
        const xs = data.map((_, i) => PAD.left + (i / Math.max(n - 1, 1)) * iW);
        const ys = data.map(d  => PAD.top  + iH - (d.count / maxVal) * iH);
        const line = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
        const fill = line
            + ` L${xs[n - 1].toFixed(1)},${(PAD.top + iH).toFixed(1)}`
            + ` L${xs[0].toFixed(1)},${(PAD.top + iH).toFixed(1)} Z`;
        return {
            line, fill,
            dots:   xs.map((x, i) => ({ x, y: ys[i], count: data[i].count })),
            labels: xs.map((x, i) => ({ x, label: data[i].month?.slice(5) || "" })),
            ticks:  [0, 0.5, 1].map(f => ({ y: PAD.top + iH - f * iH, val: Math.round(f * maxVal) })),
        };
    }, [data]);
    
    if (!pts) return <p className="idb-empty-small">No data yet.</p>;

    return (
        <svg viewBox={`0 0 ${W} ${H}`} className="idb-area-svg" preserveAspectRatio="xMidYMid meet">
            <defs>
                <linearGradient id="idb-grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={color} stopOpacity="0.22" />
                    <stop offset="100%" stopColor={color} stopOpacity="0" />
                </linearGradient>
            </defs>

            {/* Gridlines + y-labels */}
            {pts.ticks.map(t => (
                <g key={t.y}>
                    <line x1={PAD.left} x2={PAD.left + iW} y1={t.y} y2={t.y}
                        stroke="var(--color-border)" strokeWidth="1" />
                    <text x={PAD.left - 6} y={t.y + 3.5} textAnchor="end"
                        fontSize="9" fill="var(--color-text-secondary)" fontFamily="inherit">
                        {t.val}
                    </text>
                </g>
            ))}

            {/* Area */}
            <path d={pts.fill} fill="url(#idb-grad)" />
            {/* Line */}
            <path d={pts.line} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            {/* Dots */}
            {pts.dots.map((d, i) => (
                <circle key={i} cx={d.x} cy={d.y} r="3.5"
                    fill={color} stroke="var(--color-bg-primary)" strokeWidth="2" />
            ))}
            {/* X-labels */}
            {pts.labels.map((l, i) => (
                <text key={i} x={l.x} y={H - 5} textAnchor="middle"
                    fontSize="10" fill="var(--color-text-secondary)" fontFamily="inherit">
                    {l.label}
                </text>
            ))}
        </svg>
    );
};

// ── Source palette ────────────────────────────────────────────────────────────
const SOURCE_COLORS = {
    website:       "#10b981",
    referral:      "#3b82f6",
    linkedin:      "#0ea5e9",
    trade_show:    "#8b5cf6",
    cold_outreach: "#f59e0b",
    partner:       "#ec4899",
    other:         "#94a3b8",
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const InboundDashboard = () => {
    const [stats, setStats]   = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        apiClient.get("/admin/crm/inbound/stats")
            .then(res => setStats(res.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="admin-loading-wrapper"><Loading /></div>;
    if (!stats)  return <p className="crm-empty">Failed to load stats.</p>;

    const maxSource = Math.max(...(stats.bySource?.map(s => s.count) || [1]), 1);
    const today = new Date().toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
    });

    return (
        <div className="idb-wrap">

            {/* ── Page header ───────────────────────────────────────── */}
            <div className="idb-header">
                <div className="idb-header__left">
                    <h2 className="idb-header__title">Overview</h2>
                    <span className="idb-header__date">{today}</span>
                </div>
            </div>

            {/* ── Hero cards ────────────────────────────────────────── */}
            <div className="idb-hero-row">
                <div className="idb-hero-card">
                    <span className="idb-hero-card__num">
                        {stats.totalLeads.toLocaleString()}
                    </span>
                    <span className="idb-hero-card__label">Total Leads</span>
                </div>
                <div className="idb-hero-card">
                    <span className="idb-hero-card__num idb-hero-card__num--accent">
                        {stats.newThisMonth.toLocaleString()}
                    </span>
                    <span className="idb-hero-card__label">New This Month</span>
                </div>
            </div>

            {/* ── Secondary metrics strip ───────────────────────────── */}
            <div className="idb-strip">
                <div className="idb-strip__metric">
                    <span className="idb-strip__val">{stats.openRate}%</span>
                    <span className="idb-strip__key">Open Rate</span>
                </div>
                <div className="idb-strip__divider" />
                <div className="idb-strip__metric">
                    <span className="idb-strip__val">{stats.ctr}%</span>
                    <span className="idb-strip__key">CTR</span>
                </div>
                <div className="idb-strip__divider" />
                <div className="idb-strip__metric">
                    <span className="idb-strip__val">{stats.totalSent.toLocaleString()}</span>
                    <span className="idb-strip__key">Emails Sent</span>
                </div>
                <div className="idb-strip__divider" />
                <div className="idb-strip__metric">
                    <span className="idb-strip__val">{stats.segmentCount}</span>
                    <span className="idb-strip__key">Segments</span>
                </div>
                <div className="idb-strip__divider" />
                <div className="idb-strip__metric">
                    <span className="idb-strip__val">{stats.campaignCount}</span>
                    <span className="idb-strip__key">Campaigns</span>
                </div>
            </div>

            {/* ── Charts ────────────────────────────────────────────── */}
            <div className="idb-charts">

                {/* Area chart — leads over time */}
                <div className="idb-chart-card idb-chart-card--wide">
                    <div className="idb-chart-card__header">
                        <div>
                            <p className="idb-chart-card__title">New Leads</p>
                            <p className="idb-chart-card__sub">Last 6 months</p>
                        </div>
                    </div>
                    <div className="idb-chart-card__body">
                        <AreaChart data={stats.leadsOverTime || []} color="#459E74" />
                    </div>
                </div>

                {/* Source breakdown */}
                <div className="idb-chart-card">
                    <div className="idb-chart-card__header">
                        <div>
                            <p className="idb-chart-card__title">By Source</p>
                            <p className="idb-chart-card__sub">All time</p>
                        </div>
                    </div>
                    <div className="idb-chart-card__body idb-source-list">
                        {!stats.bySource?.length && (
                            <p className="idb-empty-small">No source data yet.</p>
                        )}
                        {(stats.bySource || []).map(s => {
                            const pct   = Math.round((s.count / maxSource) * 100);
                            const color = SOURCE_COLORS[s.source] || "#94a3b8";
                            return (
                                <div key={s.source} className="idb-source-row">
                                    <div className="idb-source-row__top">
                                        <span className="idb-source-dot" style={{ background: color }} />
                                        <span className="idb-source-name">
                                            {(s.source || "unknown").replace(/_/g, " ")}
                                        </span>
                                        <span className="idb-source-count">{s.count}</span>
                                    </div>
                                    <div className="idb-source-track">
                                        <div className="idb-source-fill"
                                            style={{ width: `${pct}%`, background: color }} />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default InboundDashboard;
